import { Alert, Switch, Table, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import {
  useInvalidateQueries,
  useWisecureMutation,
  useWisecureQuery,
} from '@wisecure/api-client/hooks';
import type {
  AdminFeatureFlagResponse,
  AdminFeatureFlagsResponse,
} from '@wisecure/api-client';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';

const { Text } = Typography;

/** Turns app features on or off, in the environment this dashboard is connected to. */
export function FeatureFlagsPage() {
  const invalidate = useInvalidateQueries();

  const { data, isLoading } = useWisecureQuery<AdminFeatureFlagsResponse>(
    queryKeys.featureFlags,
    (client) => client.dashboardFeatureFlags.featureFlagsDashboardControllerGetAll(),
  );

  const toggle = useWisecureMutation(
    (client, values: { key: string; enabled: boolean }) =>
      client.dashboardFeatureFlags.featureFlagsDashboardControllerSetEnabled({
        key: values.key,
        requestBody: { enabled: values.enabled },
      }),
    {
      onSuccess: () => {
        message.success('Feature flag mis à jour');
        invalidate(queryKeys.featureFlags);
      },
      onError: () => {
        message.error('Impossible de mettre à jour le feature flag');
      },
    },
  );

  return (
    <div>
      <PageHeader title="Feature flags" />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Un changement s'applique à cet environnement uniquement. Les apps le prennent en compte à leur prochaine ouverture ou à leur retour au premier plan."
      />
      <Table<AdminFeatureFlagResponse>
        rowKey="key"
        loading={isLoading}
        dataSource={data?.flags ?? []}
        pagination={false}
        columns={[
          {
            title: 'Fonctionnalité',
            key: 'label',
            render: (_, flag) => (
              <div>
                <Text strong>{flag.label}</Text>
                <div>
                  <Text type="secondary">{flag.description}</Text>
                </div>
                <Tag style={{ marginTop: 4 }}>{flag.key}</Tag>
              </div>
            ),
          },
          {
            title: 'Dernière modification',
            key: 'updatedAt',
            width: 240,
            render: (_, flag) =>
              flag.updatedBy && flag.updatedAt ? (
                <Text type="secondary">
                  {dayjs(flag.updatedAt).format('DD/MM/YYYY HH:mm')} · {flag.updatedBy}
                </Text>
              ) : (
                <Text type="secondary">—</Text>
              ),
          },
          {
            title: 'Activé',
            key: 'enabled',
            width: 90,
            render: (_, flag) => (
              <Switch
                checked={flag.enabled}
                loading={toggle.isPending}
                onChange={(enabled) => toggle.mutate({ key: flag.key, enabled })}
              />
            ),
          },
        ]}
      />
    </div>
  );
}
